import { useEffect, useRef, useState } from "react";

/** IntersectionObserver hook. `once` keeps it true after first intersection. */
export function useInView({ rootMargin = "300px", once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) setInView(false);
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, once]);
  return [ref, inView];
}

/** Calls onHit when sentinel enters the viewport (for infinite scroll). */
export function useSentinel(onHit, enabled) {
  const ref = useRef(null);
  const cb = useRef(onHit);
  cb.current = onHit;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && cb.current(), { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);
  return ref;
}
